import {useEffect, useState} from 'react'
import type {Analytics} from '@miro-site/analytics'

import {Datalayer} from '@miro-site/analytics'

export type GetAnalyticsFn = () => Promise<Analytics>
export type TemplateIdType = string | null

async function sendAccountPickerShownEvent(getAnalytics: GetAnalyticsFn) {
  try {
    const analytics = await getAnalytics()
    await analytics.sendEvent(new Datalayer('accountPickerShown', {openedFrom: 'templates'}))
  } catch (e) {
    console.error(e)
  }
}

async function sendAccountSelectedInAccountPickerEvent(
  {
    accountId,
    accountsListIndex,
    workspaceKey,
    getAnalytics,
  }: {
    accountId: string
    accountsListIndex: number
    workspaceKey: string
    getAnalytics: GetAnalyticsFn
  },
  callback?: VoidFunction,
) {
  try {
    const analytics = await getAnalytics()
    await analytics.sendEvent(
      new Datalayer('accountPickerAccountSelected', {
        accountsListIndex,
        accountId,
        workspaceKey,
      }),
    )

    if (callback) {
      callback()
    }
  } catch (e) {
    console.error(e)
  }
}

type AccountPickerLoadedCallback = (accountsIds: string[], workspaceKey: string) => void
type AccountPickerSubmitCallback = (
  accountId: string,
  accountListIndex: number,
  workspaceKey: string,
) => void
type AccountPickerCancelCallback = () => void
type AccountPickerErrorCallback = () => void

type OnAccountLoaded = (accountsIds: string | undefined, workspaceKey: string | undefined) => void
type OnAccountSubmited = (accountsIds: string | undefined, workspaceKey: string | undefined) => void

type UseAccountPicker = [
  isAccountPickerVisible: boolean,
  onAccountPickerLoaded: AccountPickerLoadedCallback,
  onAccountPickerSubmit: AccountPickerSubmitCallback,
  onAccountPickerCancel: AccountPickerCancelCallback,
  onAccountPickerError: AccountPickerErrorCallback,
]

type UseAccountPickerProps = {
  onAccountLoaded: OnAccountLoaded
  onAccountSubmited: OnAccountSubmited
  onAccountCancel: () => void
  onAccountError: () => void
  getAnalytics: GetAnalyticsFn
}

export function useAccountPicker({
  onAccountLoaded,
  onAccountSubmited,
  onAccountCancel,
  onAccountError,
  getAnalytics,
}: UseAccountPickerProps): UseAccountPicker {
  const [isAccountPickerVisible, setAccountPickerVisible] = useState<boolean>(false)

  useEffect(() => {
    if (isAccountPickerVisible) {
      sendAccountPickerShownEvent(getAnalytics)
    }
  }, [isAccountPickerVisible])

  const onAccountPickerLoaded: AccountPickerLoadedCallback = (accountsIds, workspaceKey) => {
    if (!Array.isArray(accountsIds)) {
      return
    }

    const accountsLength = accountsIds.length

    switch (accountsLength) {
      //in application user will use account creation flow
      case 0:
        onAccountLoaded(undefined, workspaceKey)
        break
      //copy board to single account. no need to select
      case 1:
        onAccountLoaded(accountsIds[0], workspaceKey)
        break
      //user will select account to copy board
      default:
        setAccountPickerVisible(true)

        break
    }
  }

  const onAccountPickerSubmit: AccountPickerSubmitCallback = (
    accountId,
    accountsListIndex,
    workspaceKey,
  ) => {
    sendAccountSelectedInAccountPickerEvent(
      {accountId, accountsListIndex, workspaceKey, getAnalytics},
      () => {
        onAccountSubmited(accountId, workspaceKey)
      },
    )
  }

  const onAccountPickerCancel: AccountPickerCancelCallback = () => {
    onAccountCancel()
    setAccountPickerVisible(false)
  }

  return [
    isAccountPickerVisible,
    onAccountPickerLoaded,
    onAccountPickerSubmit,
    onAccountPickerCancel,
    onAccountError,
  ]
}
